import type { NewsArticle, NewsIndex, NewsLocale, NewsSummary } from './types';

// Editorial content and image URLs live in R2. The repository contains only the
// reader and this public endpoint; no article index or article body is bundled.
export const NEWS_R2_BASE = (
  import.meta.env.VITE_NEWS_R2_BASE ||
  'https://pub-d8ed6542d09340a78d0e0f64c8e61b7a.r2.dev/heater/news/v1'
).replace(/\/$/, '');

const supported: NewsLocale[] = ['en', 'zh', 'ar', 'es', 'pt', 'fr', 'ru'];

export function newsLocale(language: string | undefined): NewsLocale {
  const base = (language || 'en').split('-')[0] as NewsLocale;
  return supported.includes(base) ? base : 'en';
}

async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${NEWS_R2_BASE}/${path}`, { signal });
  if (!response.ok) throw new Error(`News request failed (${response.status})`);
  return (await response.json()) as T;
}

export async function loadNewsIndex(signal?: AbortSignal): Promise<NewsSummary[]> {
  const data = await fetchJson<NewsIndex>('index.json', signal);
  if (!data || data.version !== 1 || !Array.isArray(data.items)) {
    throw new Error('Invalid news index');
  }
  return data.items
    .filter((item) => item.slug && item.translations?.en?.title && item.cover?.url)
    .sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt));
}

export async function loadNewsArticle(slug: string, signal?: AbortSignal): Promise<NewsArticle> {
  if (!/^[a-z0-9-]+$/.test(slug)) throw new Error('Invalid news article path');
  const data = await fetchJson<NewsArticle>(`articles/${slug}.json`, signal);
  if (data.slug !== slug || !data.bodies?.en) throw new Error('Invalid news article');
  return data;
}
